import {
  format,
  today,
  yesterday,
  differenceInDays,
  isSame,
  subDays,
  subWeeks,
  subMonths,
  subQuarters,
  subYears,
  startOfISOWeek,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  endOfISOWeek,
  endOfMonth,
  endOfQuarter,
  endOfYear,
} from '../../../../utils/date';

const minGrowthRatePeriod = 28;
export const minGrowthRateSelectable = 60;

export const minFloatingSelectableGrowthPeriod = () => {
  const minDate = startOfISOWeek(subDays(yesterday(), minGrowthRatePeriod));
  return differenceInDays(yesterday(), minDate);
};

const dayPeriods = [7, 30, 60, 90];
export const definedPeriods = [
  'week',
  'month',
  'quarter',
  'year',
  'month_to_date',
  'quarter_to_date',
  'year_to_date',
];
const definedPeriodsNames = {
  week: 'Previous Week',
  month: 'Previous Month',
  quarter: 'Previous Quarter',
  year: 'Previous Year',
  month_to_date: 'Month to Date',
  quarter_to_date: 'Quarter to Date',
  year_to_date: 'Year to Date',
};

export const formatFrom = (to, period) => format(subDays(to, period - 1));

export const dateRangeToSubmit = (period) =>
  ({
    week: {
      rangePeriod:
        differenceInDays(endOfISOWeek(subWeeks(today(), 1)), startOfISOWeek(subWeeks(today(), 1))) +
        1,
      from: format(startOfISOWeek(subWeeks(today(), 1))),
      to: format(endOfISOWeek(subWeeks(today(), 1))),
    },
    month: {
      rangePeriod:
        differenceInDays(endOfMonth(subMonths(today(), 1)), startOfMonth(subMonths(today(), 1))) +
        1,
      from: format(startOfMonth(subMonths(today(), 1))),
      to: format(endOfMonth(subMonths(today(), 1))),
    },
    quarter: {
      rangePeriod:
        differenceInDays(
          endOfQuarter(subQuarters(today(), 1)),
          startOfQuarter(subQuarters(today(), 1))
        ) + 1,
      from: format(startOfQuarter(subQuarters(today(), 1))),
      to: format(endOfQuarter(subQuarters(today(), 1))),
    },
    year: {
      rangePeriod: differenceInDays(
        endOfYear(subYears(today(), 1)),
        startOfYear(subYears(today(), 1))
      ),
      from: format(startOfYear(subYears(today(), 1))),
      to: format(endOfYear(subYears(today(), 1))),
    },
    month_to_date: {
      rangePeriod: differenceInDays(yesterday(), startOfMonth(today())) + 1,
      from: format(startOfMonth(today())),
      to: format(yesterday()),
    },
    quarter_to_date: {
      rangePeriod: differenceInDays(yesterday(), startOfQuarter(today())) + 1,
      from: format(startOfQuarter(today())),
      to: format(yesterday()),
    },
    year_to_date: {
      rangePeriod: differenceInDays(yesterday(), startOfYear(today())) + 1,
      from: format(startOfYear(today())),
      to: format(yesterday()),
    },
  }[period]);

const periodRange = (period, maxPeriod, minSelectablePeriod = 0) => {
  const realPeriod = differenceInDays(yesterday(), dateRangeToSubmit(period).from);
  return realPeriod <= maxPeriod && realPeriod >= minSelectablePeriod;
};

export const datesOptions = (maxPeriod, minSelectablePeriod = 0) => [
  ...dayPeriods.reduce((result, day) => {
    if (day <= maxPeriod) {
      const isGrowthPeriod = minSelectablePeriod && day < minGrowthRateSelectable;
      const periodValue = isGrowthPeriod ? minSelectablePeriod : day;
      return result.concat({
        value: periodValue,
        label: `Last ${day} days`,
        disabled: !!minSelectablePeriod && day < minGrowthRateSelectable,
      });
    }
    return result;
  }, []),
  ...definedPeriods.reduce(
    (result, period) =>
      result.concat({
        value: period,
        label: definedPeriodsNames[period],
        disabled: !periodRange(period, maxPeriod, minSelectablePeriod),
      }),
    []
  ),
];

export const getActiveOption = ({ date_from, date_to, period }) => {
  // Yesterday
  if (period === 1 && !(date_from && date_to)) {
    return {
      value: 'yesterday',
      label: 'Yesterday',
    };
  }

  if (date_from && date_to) {
    // definedPeriods
    const previousPeriods = definedPeriods.map((definedPeriod) => ({
      value: definedPeriod,
      ...dateRangeToSubmit(definedPeriod),
    }));

    const activePeriod = previousPeriods.find(
      ({ from, to }) => from === date_from && to === date_to
    );

    if (activePeriod) {
      return {
        value: activePeriod.value,
        label: definedPeriodsNames[activePeriod.value],
      };
    }
  }
  if (period && (!date_to || isSame(date_to, yesterday()))) {
    if (dayPeriods.includes(period)) {
      return {
        value: period,
        label: `Last ${period} Days`,
      };
    }
    const dateTo = date_to || format(yesterday());
    const dateFrom = format(subDays(dateTo, period - 1));
    const isSameDate = dateFrom === dateTo;
    return { value: 'custom', label: isSameDate ? dateFrom : `${dateFrom} - ${dateTo}` };
  }
  const isSameDate = date_from === date_to;
  return { value: 'custom', label: isSameDate ? date_from : `${date_from} - ${date_to}` };
};
